import React, { createContext, useCallback, useState, useContext } from 'react';
import api from '../services/api';

import { useToast } from './toast';
import { useClinica } from './clinica';

import getResponseErrors from '../utils/getResponseErrors';

export interface User {
  UsuCodigo: number;
  UsuNome: string;
  UsuEmail: string;
  avatar_url: string;
  UsuTipo: number;
  UsuClinica: number;
  UsuPermissao: string;
}

export interface Clinica {
  CliAtivo: boolean;
  CliBairro: string;
  CliCEP: string;
  CliCNPJ: string;
  CliCidade: string;
  CliCidadeCodigo: string;
  CliCodigo: number;
  CliCodigoRioSulWeb: string;
  CliComplemento: string;
  CliContrato: boolean;
  CliCor: string;
  CliEmail: string;
  CliEmiteNotaFiscal: boolean;
  CliEmiteNFAutomatica: boolean;
  CliEmiteNFCartao: boolean;
  CliEndereco: string;
  CliFacebook: string;
  CliIE: string;
  CliInstagram: string;
  CliMedicoAgendaCompleta: boolean;
  CliNegociacaoCartao: boolean;
  CliNome: string;
  CliNomeAbreviado: string;
  CliNumero: string;
  CliOrdenaPorPagamento: boolean;
  CliPagamentoAntecipado: boolean;
  CliProAnamnese: boolean;
  CliProAnexo: boolean;
  CliProDocumento: boolean;
  CliRazaoSocial: string;
  CliSimplesNacional: boolean;
  CliSite: string;
  CliTelefone: string;
  CliTelefone2: string;
  CliUF: string;
  CliValidaDadosNF: boolean;
  CliDrSaude: boolean;
  CliRepresentante: string;
  CliDiasVencimento?: string;
  CliLembreteVencimento?: boolean;
  CliLembreteVencido5?: boolean;
  CliLembreteVencido10?: boolean;
  CliIdentificaPedidoExame?: boolean;
  CliFuncionaSabado?: boolean;
  CliAtendeConvenio: boolean;
  CliExibeConvenioMedico: boolean;
  CliPerfilAlteraNomeAssociado: number;
  CliPerfilAcessaProcedimentos: number;
  CliPerfilValorCartao: number;
  CliPerfilCancelaCartao: number;
  CliUsaPainel: boolean;
  CliCobrancaAutomatica: boolean;
  ConfigBoleto: {
    Emite: boolean;
    EmiteOnLine: boolean;
    Email: {
      Envia: boolean;
      EmailEnvio: string;
      EmailResposta: string;
      Dias: number;
    };
    WhatsApp: {
      Envia: boolean;
      Dias: number;
    };
  };
  IntegracaoWhatsapp?: boolean;
  AtivaNegociacaoCobranca?: boolean;
}
interface AuthState {
  token: string;
  user: User;
  clinica: Clinica;
}

interface SignInCredentials {
  email: string;
  password: string;
  codClinica: number;
}

interface AuthContextData {
  user: User;
  clinica: Clinica;
  signIn(credenctials: SignInCredentials): Promise<void>;
  signOut(): void;
  updateUser(user: User): void;
}

const AuthContext = createContext<AuthContextData>({} as AuthContextData);

const AuthProvider: React.FC = ({ children }) => {
  const { addToast } = useToast();
  const { imagensClinica } = useClinica();

  const semFoto =
    imagensClinica.find(img => img.nome === 'semFoto')?.arquivo_url || '';

  const [data, setData] = useState<AuthState>(() => {
    const token = localStorage.getItem('@AgendaFacil:token');
    const user = localStorage.getItem('@AgendaFacil:user');
    const clinica = localStorage.getItem('@AgendaFacil:clinica');
    if (token && user && clinica) {
      api.defaults.headers.authorization = `Bearer ${token}`;
      // api.defaults.headers.codClinica = `1`;

      return { token, user: JSON.parse(user), clinica: JSON.parse(clinica) };
    }

    return {} as AuthState;
  });

  const signIn = useCallback(
    async ({ email, password, codClinica }) => {
      try {
        const response = await api.post('sessions', {
          UsuLogin: email,
          UsuSenha: password,
          UsuClinica: codClinica,
        });

        const { token, user, clinica } = response.data;

        if (!user.avatar_url) user.avatar_url = semFoto;

        localStorage.setItem('@AgendaFacil:token', token);
        localStorage.setItem('@AgendaFacil:user', JSON.stringify(user));
        localStorage.setItem('@AgendaFacil:clinica', JSON.stringify(clinica));

        api.defaults.headers.authorization = `Bearer ${token}`;
        api.defaults.headers.codClinica = user.UsuClinica;

        setData({ token, user, clinica });
      } catch (error) {
        const mensagemErro = getResponseErrors(error);

        addToast({
          type: 'error',
          title: 'Erro na autenticação',
          description: mensagemErro,
        });
      }
    },
    [addToast, semFoto],
  );

  const signOut = useCallback(() => {
    localStorage.removeItem('@AgendaFacil:token');
    localStorage.removeItem('@AgendaFacil:user');
    localStorage.removeItem('@AgendaFacil:clinica');
    // localStorage.clear();

    setData({} as AuthState);
  }, []);

  const updateUser = useCallback(
    (user: User) => {
      localStorage.setItem('@AgendaFacil:user', JSON.stringify(user));

      setData({
        token: data.token,
        user,
        clinica: data.clinica,
      });
    },
    [data.token, data.clinica],
  );

  return (
    <AuthContext.Provider
      value={{
        user: data.user,
        clinica: data.clinica,
        signIn,
        signOut,
        updateUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

function useAuth(): AuthContextData {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth deve ser usado com o AuthProvider');
  }

  return context;
}

export { AuthProvider, useAuth };
